import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";

const HRDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/hr/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching HR stats:', error);
      toast.error('Erro ao carregar estatísticas de RH');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const cards = [
    {
      title: 'Colaboradores',
      icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
      color: 'from-blue-500 to-cyan-500',
      stats: [
        { label: 'Total', value: stats?.employees?.total || 0 },
        { label: 'De licença', value: stats?.employees?.on_leave || 0, highlight: stats?.employees?.on_leave > 0 }
      ]
    },
    {
      title: 'Onboarding/Offboarding',
      icon: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z',
      color: 'from-emerald-500 to-teal-500',
      path: '/hr/lifecycle',
      stats: [
        { label: 'Onboarding', value: stats?.employees?.pending_onboarding || 0, highlight: stats?.employees?.pending_onboarding > 0 },
        { label: 'Offboarding', value: stats?.employees?.pending_offboarding || 0, highlight: stats?.employees?.pending_offboarding > 0 }
      ]
    },
    {
      title: 'Férias',
      icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      color: 'from-cyan-500 to-blue-500',
      path: '/hr/absences',
      stats: [
        { label: 'De Férias Agora', value: stats?.vacations?.on_vacation || 0, highlight: stats?.vacations?.on_vacation > 0 },
        { label: 'Pedidos Pendentes', value: stats?.vacations?.pending_requests || 0, highlight: stats?.vacations?.pending_requests > 0 }
      ]
    },
    {
      title: 'Ausências',
      icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
      color: 'from-amber-500 to-orange-500',
      path: '/hr/absences',
      stats: [
        { label: 'Pendentes', value: stats?.absences?.pending || 0, highlight: stats?.absences?.pending > 0 },
        { label: 'Aguardam RH', value: stats?.absences?.awaiting_hr || 0, highlight: stats?.absences?.awaiting_hr > 0 }
      ]
    },
    {
      title: 'Viaturas',
      icon: 'M8 7h12m0 0a3 3 0 110 6h-2m-10 0a3 3 0 110-6m10 6H6a3 3 0 01-3-3V7a3 3 0 013-3h12a3 3 0 013 3v3M6 17a3 3 0 100-6m0 6a3 3 0 110-6m0 6v1a1 1 0 001 1h1m10-1a1 1 0 01-1 1h-1m-7-6h8',
      color: 'from-green-500 to-emerald-600',
      path: '/hr/vehicles',
      stats: [
        { label: 'Total', value: stats?.vehicles?.total || 0 },
        { label: 'Atribuídas', value: stats?.vehicles?.assigned || 0 }
      ]
    },
    {
      title: 'Coimas',
      icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
      color: 'from-red-500 to-rose-600',
      path: '/hr/vehicles',
      stats: [
        { label: 'Pendentes', value: stats?.fines?.pending || 0, highlight: stats?.fines?.pending > 0 },
        { label: 'Valor Pend.', value: `€${stats?.fines?.pending_value || 0}`, highlight: (stats?.fines?.pending_value || 0) > 0, isValue: true }
      ]
    },
    {
      title: 'Assiduidade',
      icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
      color: 'from-purple-500 to-pink-500',
      path: '/hr/attendance',
      stats: [
        { label: 'Por validar', value: stats?.time_records?.pending_validation || 0, highlight: stats?.time_records?.pending_validation > 0 }
      ]
    },
    {
      title: 'Tickets IT',
      icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
      color: 'from-slate-500 to-gray-600',
      path: '/hr/tickets',
      stats: [
        { label: 'Abertos', value: stats?.it_tickets?.open || 0, highlight: stats?.it_tickets?.open > 0 }
      ]
    },
    {
      title: 'Recrutamento',
      icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
      color: 'from-indigo-500 to-violet-500',
      path: '/hr/recruitment',
      stats: [
        { label: 'Vagas abertas', value: stats?.recruitment?.open_jobs || 0 },
        { label: 'Candidatos ativos', value: stats?.recruitment?.active_candidates || 0 }
      ]
    },
    {
      title: 'Avaliações',
      icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      color: 'from-rose-500 to-red-500',
      path: '/hr/reviews',
      stats: [
        { label: 'Pendentes', value: stats?.performance?.pending_reviews || 0, highlight: stats?.performance?.pending_reviews > 0 }
      ]
    }
  ];

  return (
    <div data-testid="hr-dashboard-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Gestão de Recursos Humanos</h1>
          <p className="text-muted-foreground">Painel de controlo de RH</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchStats}
            className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Atualizar
          </button>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {cards.map((card, index) => (
          <div
            key={index}
            onClick={() => card.path && navigate(card.path)}
            className={`rounded-xl border bg-card p-5 ${card.path ? 'cursor-pointer hover:border-primary/50 transition-all hover:shadow-lg' : ''} group`}
            data-testid={`hr-card-${card.title.toLowerCase().replace(/\//g, '-')}`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center ${card.path ? 'group-hover:scale-110 transition-transform' : ''}`}>
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={card.icon} />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-lg">{card.title}</h3>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {card.stats.map((stat, idx) => (
                <div key={idx} className="text-center p-2 rounded-lg bg-muted/50">
                  <p className={`text-2xl font-bold ${stat.highlight ? 'text-amber-500' : ''}`}>{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Pending Validations */}
        <div className="rounded-xl border bg-card p-6">
          <h2 className="font-semibold text-lg mb-4">Ações Rápidas</h2>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate('/hr/attendance')}
              className="p-4 rounded-lg border border-border hover:bg-muted transition-colors text-left"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                {stats?.time_records?.pending_validation > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">{stats.time_records.pending_validation}</span>
                )}
              </div>
              <p className="font-medium">Validar Assiduidade</p>
              <p className="text-xs text-muted-foreground">Registos manuais pendentes</p>
            </button>

            <button
              onClick={() => navigate('/hr/absences')}
              className="p-4 rounded-lg border border-border hover:bg-muted transition-colors text-left"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                {(stats?.absences?.pending || 0) + (stats?.absences?.awaiting_hr || 0) > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                    {(stats?.absences?.pending || 0) + (stats?.absences?.awaiting_hr || 0)}
                  </span>
                )}
              </div>
              <p className="font-medium">Aprovar Ausências</p>
              <p className="text-xs text-muted-foreground">Pedidos pendentes</p>
            </button>

            <button
              onClick={() => navigate('/hr/recruitment')}
              className="p-4 rounded-lg border border-border hover:bg-muted transition-colors text-left"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>
              </div>
              <p className="font-medium">Criar Vaga</p>
              <p className="text-xs text-muted-foreground">Nova oportunidade</p>
            </button>

            <button
              onClick={() => navigate('/hr/reviews')}
              className="p-4 rounded-lg border border-border hover:bg-muted transition-colors text-left"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-rose-100 dark:bg-rose-900/30">
                  <svg className="w-5 h-5 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <p className="font-medium">Nova Avaliação</p>
              <p className="text-xs text-muted-foreground">Criar avaliação</p>
            </button>
          </div>
        </div>

        {/* Recent Activity Placeholder */}
        <div className="rounded-xl border bg-card p-6">
          <h2 className="font-semibold text-lg mb-4">Resumo</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium">Total de Colaboradores</p>
                  <p className="text-xs text-muted-foreground">Ativos na empresa</p>
                </div>
              </div>
              <p className="text-2xl font-bold">{stats?.employees?.total || 0}</p>
            </div>

            <div 
              onClick={() => navigate('/hr/recruitment')}
              className="flex items-center justify-between p-3 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors group"
              data-testid="open-jobs-card"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 group-hover:scale-110 transition-transform">
                  <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium">Vagas Abertas</p>
                  <p className="text-xs text-muted-foreground">Em recrutamento</p>
                </div>
              </div>
              <p className="text-2xl font-bold">{stats?.recruitment?.open_jobs || 0}</p>
            </div>

            <div 
              onClick={() => navigate('/hr/recruitment')}
              className="flex items-center justify-between p-3 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors group"
              data-testid="active-candidates-card"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 group-hover:scale-110 transition-transform">
                  <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium">Candidatos Ativos</p>
                  <p className="text-xs text-muted-foreground">No pipeline</p>
                </div>
              </div>
              <p className="text-2xl font-bold">{stats?.recruitment?.active_candidates || 0}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRDashboardPage;
